import { Component, output } from '@angular/core';
import { effect, ElementRef, inject, input } from '@angular/core';
import { DomEvent, DomUtil, LatLng, Layer, Map, Point } from 'leaflet';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { Position } from '../models/geometry';
import { LMapService } from '../services/l-map.service';


export interface Popup {
    position: Position;
    onMarker: boolean;
    markerId?: string;
}


class LPopup extends Layer {

    private _container: HTMLElement;

    private _latLng: LatLng;

    private removed = new Subject<void>();

    private mql = window.matchMedia('(max-width: 512px)');

    private fixed = this.mql.matches;

    constructor(options: { content: HTMLElement }) {
        super();
        const element = options.content;

        this._container = DomUtil.create('div');
        this._container.style.position = 'absolute';
        this._container.style.top = '0';
        this._container.style.left = '0';
        this._container.classList.add('leaflet-zoom-animated');
        this._container.style.pointerEvents = 'none';

        this._container.style.zIndex = '700';

        this._container.appendChild(options.content);

        DomEvent.disableClickPropagation(element);
        DomEvent.disableScrollPropagation(element);
        DomEvent.on(element, 'contextmenu', DomEvent.stopPropagation);
    }

    onAdd(map: Map) {
        this.addContainer(map);

        fromEvent<MediaQueryListEvent>(this.mql, 'change').pipe(takeUntil(this.removed)).subscribe(event => {
            this.removeContainer(this._map);
            this.fixed = event.matches;
            this.addContainer(this._map);
        });
        return this;
    }

    onRemove(map: Map) {
        this.removed.next();
        this.removeContainer(map);
        return this;
    }

    private addContainer(map: Map) {
        if (this.fixed) {
            map.getContainer().appendChild(this._container);
            this._container.style.top = '';
            this._container.style.right = '0';
            this._container.style.bottom = '0';
        } else {
            map.getPane('popupPane')?.appendChild(this._container);
            map.on('zoom', this._updatePosition, this);
            map.on('viewreset', this._updatePosition, this);
            map.on('zoomanim', this._animateZoom, this);
            this._container.style.top = '0';
            this._container.style.right = '';
            this._container.style.bottom = '';
        }
        this._updatePosition();
    }

    private removeContainer(map: Map) {
        if (!this.fixed) {
            map.off('zoom', this._updatePosition, this);
            map.off('viewreset', this._updatePosition, this);
            map.off('zoomanim', this._animateZoom, this);
        }
        this._container.remove();
    }

    getLatLng() {
        return this._latLng;
    }

    setLatLng(latLng: LatLng) {
        this._latLng = latLng;
        if (this._map) {
            this._updatePosition();
        }
        return this;
    }

    _animateZoom(e: any) {
        const pos = this.fixed ? new Point(0, 0) : (this._map as any)._latLngToNewLayerPoint(this._latLng, e.zoom, e.center);
        DomUtil.setPosition(this._container, pos);
    }

    _updatePosition() {
        if (!this._map) {
            return;
        }

        const pos = this.fixed ? new Point(0, 0) : this._map.latLngToLayerPoint(this._latLng);
        DomUtil.setPosition(this._container, pos);
    }
}


@Component({
    selector: 'l-popup',
    styles: [
        `
        .popup {
            transform: translate(-50%, -100%);
        }
        .content {
            pointer-events: auto;
        }

        .on-marker {
            position: relative;
            bottom: 41px;
        }

        .content {
            width: 400px;
            border-bottom-right-radius: 5px;
            border-bottom-left-radius: 5px;
            font-size: 90%;
            background: white;
            max-height: 380px;
            overflow-y: auto;
            overflow-x: hidden;
        }

        .inner {
            padding: 6px 5px;
        }

        .close {
            position: absolute;
            right: 10px;
            top: 2px;
            z-index: 100;
            background: #ffffff;
            opacity: .4;
            color: #383636;
            transform-origin: 100% 0;
        }
        .close:hover {
            opacity: 1;
            transform: scale(1.2);
        }

        .arrow {
            width: 28px;
            height: 20px;
            margin: auto;
            position: relative;
            top: -1px;  /* prevent showing any borders */
            pointer-events: none;
        }
        .arrow::after {
            content: '';
            display: block;
            width: 0;
            height: 0;
            border: 14px solid transparent;
            border-top: 14px solid white;
            border-bottom: none;
        }
        @media (max-width: 512px) {
            .popup {
                transform: none;
            }
            .on-marker {
                bottom: 0;
            }
            .arrow {
                display: none;
            }
            .content {
                border-radius: 0;
                width: 100%;
                max-height: 35vh;
            }
        }

        .top-bar-primary-sm::before {
            transform: translateY(2px);
            margin-bottom: 3px;
        }
        `,
    ],
    template: `
        <div class="popup" [class.on-marker]="popup().onMarker" animate.enter="fade-enter" animate.leave="fade-leave" style="--fade-duration: 0.2s">
            <div class="content top-bar-primary-sm shadow">
                <div class="inner">
                    @if (closeButton()) {
                        <button (click)="close.emit()" tabindex="-1" type="button" class="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    }
                    <ng-content></ng-content>
                </div>
            </div>
            <div class="arrow"></div>
        </div>
    `,
})
export class PopupComponent {

    popup = input.required<Popup>();

    closeButton = input(true);

    close = output<void>();

    protected lPopup: LPopup;

    private el = inject(ElementRef);

    private mapService = inject(LMapService);

    constructor() {
        effect(() => {
            const popup = this.popup();
            const [lng, lat] = popup.position;
            this.lPopup?.setLatLng(new LatLng(lat, lng));
        });
    }

    ngOnInit() {
        this.lPopup = new LPopup({
            content: this.el.nativeElement,
        });

        const popup = this.popup();
        const [lng, lat] = popup.position;
        this.lPopup.setLatLng(new LatLng(lat, lng));

        this.lPopup.addTo(this.mapService.getMap());
    }

    ngOnDestroy() {
        this.lPopup.remove();
    }
}
